import { useEffect, useState } from "react";
import { Video as VideoIcon } from "lucide-react";
import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { BaseballTrendHero } from "@/components/flowly/BaseballTrend/BaseballTrendHero";
import { BaseballTrendPresetGrid } from "@/components/flowly/BaseballTrend/BaseballTrendPresetGrid";
import { BaseballTrendWardrobePicker } from "@/components/flowly/BaseballTrend/BaseballTrendWardrobePicker";
import { BaseballTrendAspectRatioPicker } from "@/components/flowly/BaseballTrend/BaseballTrendAspectRatioPicker";
import { BaseballTrendPreviewGrid } from "@/components/flowly/BaseballTrend/BaseballTrendPreviewGrid";
import { CreditCostPill } from "@/components/flowly/CreditCostPill/CreditCostPill";
import { BaseballTrendPreviewHistory } from "@/components/flowly/BaseballTrend/BaseballTrendPreviewHistory";
import { BaseballTrendActionPicker } from "@/components/flowly/BaseballTrend/BaseballTrendActionPicker";
import { FlowReadyCta } from "@/components/flowly/FlowReadyCta/FlowReadyCta";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { VideoLightbox } from "@/components/flowly/Lightbox/VideoLightbox/VideoLightbox";
import { cn } from "@/lib/utils";
import { apiGet } from "@/lib/api/apiClient";
import { downloadMedia } from "@/lib/mediaUtils";
import { BASEBALL_TREND_DURATIONS } from "@/config/baseballTrend";
import {
  BASEBALL_TREND_CONFIG_ANCHOR_ID,
  BASEBALL_TREND_PRESETS_ANCHOR_ID,
  BASEBALL_TREND_WARDROBE_ANCHOR_ID,
  BASEBALL_TREND_ACTION_ANCHOR_ID,
} from "./constants";
import type {
  BaseballTrendPresetId,
  BaseballTrendPresetsResponse,
} from "@shared/schemas/api";
import { useBaseballTrend } from "./useBaseballTrend";

interface PresetTile {
  id: BaseballTrendPresetId;
  nameKey: string;
  previewImage: string;
}

export default function BaseballTrendPage() {
  const [presets, setPresets] = useState<PresetTile[]>([]);

  const {
    upload,
    isUploading,
    presetId,
    wardrobeMode,
    customWardrobe,
    previewUrls,
    selectedPreviewIndex,
    selectedHistoricalUrl,
    pastPreviewImages,
    isPreviewing,
    previewStartedAt,
    actionMode,
    customAction,
    duration,
    aspectRatio,
    isGenerating,
    galleryVideos,
    dbPendingVideos,
    videoCompletingIds,
    pendingCount,
    selectedMedia,
    handleAddFile,
    handleRemoveUpload,
    handlePresetSelect,
    handleWardrobeModeChange,
    handleCustomWardrobeChange,
    handleGeneratePreview,
    handlePreviewSelect,
    handleHistoricalPreviewSelect,
    handleActionModeChange,
    handleCustomActionChange,
    handleDurationChange,
    handleAspectRatioChange,
    handleGenerate,
    handleOpenLightbox,
    handleCloseLightbox,
    handleRegenerate,
    handleDownload,
    handleShare,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    canGenerate,
    canGeneratePreview,
    renderCost,
    previewSlots,
    previewCost,
    t,
  } = useBaseballTrend();

  // Fetch the preset metadata (id/nameKey/previewImage) on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiGet<BaseballTrendPresetsResponse>(
          "/api/baseball-trend-presets",
          { auth: false },
        );
        if (cancelled) return;
        const tiles: PresetTile[] = resp.presets
          .filter(
            (p): p is { id: BaseballTrendPresetId; nameKey: string; previewImage: string } =>
              !!p.id && !!p.nameKey && p.previewImage !== undefined,
          )
          .map((p) => ({ id: p.id, nameKey: p.nameKey, previewImage: p.previewImage }));
        setPresets(tiles);
      } catch {
        /* non-fatal — the picker will just show the empty grid until the user retries */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasUpload = !!upload;
  const wardrobeReady =
    wardrobeMode === "default" ||
    (wardrobeMode === "custom" && customWardrobe.trim().length > 0);
  const showPreviewStep = hasUpload && !!presetId && wardrobeReady;
  const showActionStep =
    selectedPreviewIndex !== null || selectedHistoricalUrl !== null;

  const durationOptions = BASEBALL_TREND_DURATIONS.map((d) => ({
    value: d,
    label:
      d === 5
        ? t.baseballTrend.durationFiveSec
        : t.baseballTrend.durationTenSec,
  }));

  return (
    <StudioPageLayout
      activeTab="trends"
      activeApp="baseballTrend"
      footer={
        !selectedMedia ? (
          <GenerationBar
            onGenerate={handleGenerate}
            generating={isGenerating}
            disabled={!canGenerate}
            creditCost={renderCost}
            buttonLabel={t.baseballTrend.generateBtn}
            mediaType="image"
            // Aspect ratio is picked earlier (step 4) so the previews are
            // already framed correctly — hide the chip in the footer to
            // prevent late changes that would invalidate the picked preview.
            aspectRatio={aspectRatio}
            onAspectRatioChange={() => undefined}
            availableAspectRatios={[]}
            videoDuration={duration}
            onVideoDurationChange={(v) => handleDurationChange(v as 5 | 10)}
            availableDurations={durationOptions}
          />
        ) : undefined
      }
      lightbox={
        selectedMedia ? (
          <VideoLightbox
            videoUrl={selectedMedia.url}
            sourceImageUrl={selectedMedia.referenceImageUrls[0]}
            prompt={selectedMedia.prompt}
            createdAt={selectedMedia.createdAt}
            model={selectedMedia.model}
            resolution={selectedMedia.resolution}
            aspectRatio={selectedMedia.aspectRatio}
            duration={selectedMedia.duration}
            pipeline={selectedMedia.pipeline}
            onClose={handleCloseLightbox}
            onRegenerate={handleRegenerate}
            onDownload={handleDownload}
            onShare={handleShare}
            navigation={
              galleryVideos.length > 1
                ? {
                    currentIndex: currentMediaIndex,
                    totalItems: galleryVideos.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
    >
      <div className="space-y-6 md:space-y-8 mt-2 md:mt-4">
        <BaseballTrendHero
          upload={upload}
          isUploading={isUploading}
          canGenerate={canGenerate}
          onAddFile={handleAddFile}
          onRemove={handleRemoveUpload}
          onGenerate={handleGenerate}
        />

        {hasUpload && (
          <div
            id={BASEBALL_TREND_CONFIG_ANCHOR_ID}
            className="scroll-mt-24 space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            {/* Step 2 — Pick the scene */}
            <div
              id={BASEBALL_TREND_PRESETS_ANCHOR_ID}
              className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5"
            >
              <BaseballTrendPresetGrid
                presets={presets}
                selectedId={presetId}
                onSelect={handlePresetSelect}
              />
            </div>

            {/* Step 3 — Wardrobe / props (appears after a preset is picked) */}
            {presetId && (
              <div
                id={BASEBALL_TREND_WARDROBE_ANCHOR_ID}
                className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
              >
                <BaseballTrendWardrobePicker
                  mode={wardrobeMode}
                  customWardrobe={customWardrobe}
                  onModeChange={handleWardrobeModeChange}
                  onCustomWardrobeChange={handleCustomWardrobeChange}
                />
              </div>
            )}

            {/* Step 4 — Aspect ratio (landscape vs portrait). Lives BEFORE
                preview generation because both stages render in this ratio. */}
            {presetId && (
              <div className="rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <BaseballTrendAspectRatioPicker
                  value={aspectRatio}
                  onChange={handleAspectRatioChange}
                />
              </div>
            )}

            {/* Step 5 — Generate + pick a preview pair */}
            {showPreviewStep && (
              <div className="rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-4">
                {/* Initial state — show only the "Generate previews" CTA. Once
                    we have urls (or are mid-generation) hand off to the grid. */}
                {previewUrls.length === 0 && !isPreviewing ? (
                  <div className="space-y-4">
                    <div className="text-center">
                      <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
                        {t.baseballTrend.previewTitle}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        {t.baseballTrend.previewHint}
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleGeneratePreview}
                        disabled={!canGeneratePreview}
                        className={cn(
                          // Neutral gray on purpose — this is an intermediate
                          // "Stage 1" CTA. The bold gradient is reserved for
                          // the final render button in the GenerationBar so
                          // the visual hierarchy reads: gray = "next step",
                          // gradient = "finish".
                          "inline-flex items-center gap-2 px-5 md:px-6 h-11 md:h-12 rounded-xl bg-zinc-800 hover:bg-zinc-900 text-white text-sm md:text-base font-semibold transition-colors shadow-sm",
                          !canGeneratePreview && "opacity-60 cursor-not-allowed hover:bg-zinc-800",
                        )}
                      >
                        <span>{t.baseballTrend.previewGenerateBtn}</span>
                        <CreditCostPill cost={previewCost} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <BaseballTrendPreviewGrid
                    imageUrls={previewUrls}
                    selectedIndex={selectedPreviewIndex}
                    isGenerating={isPreviewing}
                    generationStartedAt={previewStartedAt}
                    slots={previewSlots}
                    onSelect={handlePreviewSelect}
                    onRegenerate={handleGeneratePreview}
                    canRegenerate={canGeneratePreview}
                    previewCost={previewCost}
                  />
                )}

                {/* Past-previews picker — surfaces every prior baseball_trend
                    preview the user has generated so they can re-render an
                    older one without paying for a fresh pair. Hidden when
                    there's no history yet (component returns null). */}
                <BaseballTrendPreviewHistory
                  images={pastPreviewImages}
                  excludeUrls={previewUrls}
                  selectedUrl={selectedHistoricalUrl}
                  onSelect={handleHistoricalPreviewSelect}
                  aspectRatio={aspectRatio}
                />
              </div>
            )}

            {/* Step 5 — Motion (after the user picks a preview) */}
            {showActionStep && (
              <div
                id={BASEBALL_TREND_ACTION_ANCHOR_ID}
                className="scroll-mt-24 rounded-2xl border border-brand/30 bg-brand/[0.04] p-4 md:p-5 animate-in fade-in slide-in-from-bottom-2 duration-300"
              >
                <BaseballTrendActionPicker
                  mode={actionMode}
                  customAction={customAction}
                  onModeChange={handleActionModeChange}
                  onCustomActionChange={handleCustomActionChange}
                />
              </div>
            )}

            {/* Inline "all set" CTA — same generate action as the footer bar.
                Mounted through the render too so it doesn't flicker. */}
            {(canGenerate || isGenerating) && (
              <FlowReadyCta
                onGenerate={handleGenerate}
                generating={isGenerating}
                disabled={!canGenerate}
                creditCost={renderCost}
                buttonLabel={t.baseballTrend.generateBtn}
              />
            )}
          </div>
        )}

        <CreationsGallery
          title={t.baseballTrend.results}
          pendingCount={pendingCount}
          pendingLabel={t.generate.creatingVideo}
          pendingItems={dbPendingVideos.map((v) => ({
            id: v.id,
            createdAt: v.created_at,
          }))}
          pendingMediaType="video"
          completingIds={videoCompletingIds}
          className="mt-2 mb-4"
          emptyIcon={VideoIcon}
          emptyTitle={t.baseballTrend.noResults}
        >
          {galleryVideos.map((video) => (
            <MediaGridItem
              key={video.id}
              imageUrl={video.displayUrl || undefined}
              videoUrl={video.url || undefined}
              isVideo
              alt={video.prompt}
              onClick={() => handleOpenLightbox(video)}
              onDownload={
                video.url
                  ? () => downloadMedia(video.url!, "baseball-trend", "mp4")
                  : undefined
              }
              downloadLabel={t.baseballTrend.downloadAria}
            />
          ))}
        </CreationsGallery>
      </div>
    </StudioPageLayout>
  );
}
